import { getActiveImportById } from 'config/requests/bankOperations';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { useApproveImport } from 'shared/widgets/RequestsBankOpsImport/hooks/useApproveImport';
import { ACTIVE_IMPORT_ID_KEY } from 'shared/widgets/RequestsBankOpsImport/hooks/useGetActiveImportId';
import { useRejectImport } from 'shared/widgets/RequestsBankOpsImport/hooks/useRejectImport';
import { ImportFile, ImportFileStatuses } from 'types/embedded/bankOperation';
import { getLastUrlSegment } from 'utils/getLastUrlSegment';

type UseImportActionsProps = {
  activeImport: ImportFile | undefined;
  onSuccess?: (file: ImportFile) => void;
  onClose?: () => void;
};

const POLL_INTERVAL = 5000;
const POLL_ATTEMPTS = 24;

const FINAL_STATUSES: string[] = [
  ImportFileStatuses.Saved,
  ImportFileStatuses.Fail,
  ImportFileStatuses.Rejected,
];

const pollFinalImportStatus = async (id: string): Promise<string | null> => {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    const data = await getActiveImportById(id);
    const status = getLastUrlSegment(data?.status);
    if (status && FINAL_STATUSES.includes(status)) {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
  }
  return null;
};

export const useImportActions = ({ activeImport, onSuccess, onClose }: UseImportActionsProps) => {
  const notify = useNotify();

  const { mutate: approveImport, isPending: isApproving } = useApproveImport();
  const { mutate: rejectImport, isPending: isRejecting } = useRejectImport();

  const handleApprove = () => {
    if (!activeImport?.id) {
      return;
    }

    approveImport(
      { id: activeImport.id },
      {
        onSuccess: async () => {
          try {
            onClose?.();

            const status = await pollFinalImportStatus(activeImport.id);

            await queryClient.invalidateQueries({
              queryKey: [ACTIVE_IMPORT_ID_KEY],
            });

            if (status === ImportFileStatuses.Saved) {
              setTimeout(() => {
                queryClient.invalidateQueries({
                  queryKey: ['bank_operations/pending_statuses'],
                });
              }, 5000);
              onSuccess?.(activeImport);
            }
          } catch {
            console.error('Ошибка при получении статуса');
          }
        },
        onError: (error: any) => {
          notify(`Ошибка при загрузке: ${error.data?.errors[0]?.title}`, { type: 'error' });
        },
      },
    );
  };

  const handleReject = () => {
    if (!activeImport?.id) {
      return;
    }

    rejectImport(
      { id: activeImport.id },
      {
        onSuccess: () => {
          notify('Загрузка отменена', { type: 'success' });
          onClose?.();
          setTimeout(() => {
            queryClient.resetQueries({ queryKey: [ACTIVE_IMPORT_ID_KEY] });
          }, 5000);
        },
        onError: (error: any) => {
          notify(`Ошибка при отмене загрузки: ${error.data?.errors[0]?.title}`, { type: 'error' });
        },
      },
    );
  };

  return { handleApprove, handleReject, isApproving, isRejecting };
};
